import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';

import { verifyEmailOtp } from 'services/auth/verifyEmailOtp';
import { resendEmailOtp } from 'services/auth/resendEmailOtp';
import useAuth from 'hooks/useAuth';
import AuthWrapper from 'sections/auth/AuthWrapper';

// ================================|| EMAIL OTP VERIFICATION ||================================ //

const EmailOtpVerification = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { login } = useAuth();

  const email = state?.email;
  const password = state?.password;
  const [otp, setOtp] = useState('');

  const verifyMutation = useMutation({
    mutationFn: ({ email, otp }) => verifyEmailOtp({ email, verificationCode: otp }),
    onSuccess: async (data) => {
      if (data?.status) {
        toast.success(data?.message || 'OTP Verified');
        try {
          const res = await login(email, password);
          if (res?.status || res?.success) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        } catch (e) {
          navigate('/login', { replace: true });
        }
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'OTP Verification failed');
    }
  });

  const resendOtpMutation = useMutation({
    mutationFn: resendEmailOtp,
    onSuccess: (data) => toast.success(data?.message),
    onError: (err) => toast.error(err?.response?.data?.message)
  });

  return (
    <AuthWrapper>
      <Stack spacing={2.5}>
        {/* Header */}
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="h4" fontWeight={600} color="text.primary" textAlign="center">
            Verify Your Email
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Enter the 6-digit OTP sent to{' '}
            <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
              {email}
            </Typography>
          </Typography>
        </Stack>

        <Divider />

        {/* OTP input */}
        <TextField
          label="6-digit OTP"
          type="text"
          fullWidth
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              fontSize: '22px',
              letterSpacing: '10px',
              fontWeight: 600,
            }
          }}
        />

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => verifyMutation.mutate({ email, otp })}
          disabled={verifyMutation.isLoading || otp.length !== 6}
          sx={{ py: 1.4, fontSize: '1rem', fontWeight: 600, borderRadius: '4px' }}
        >
          {verifyMutation.isLoading ? <CircularProgress size={22} color="inherit" /> : 'Verify Email'}
        </Button>

        <Typography variant="body2" color="text.secondary" textAlign="center">
          Didn&apos;t receive the OTP?{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary.main"
            fontWeight={600}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => resendOtpMutation.mutate({ email })}
          >
            Resend
          </Typography>
        </Typography>
      </Stack>
    </AuthWrapper>
  );
};

export default EmailOtpVerification;